import { Icon } from "@iconify/react";
import { Box, Chip, ListSubheader } from "@mui/material";
import Modal from "@mui/material/Modal";
import { styled } from "@mui/material/styles";
import React, { useCallback, useMemo, useState } from "react";
import { Departments } from "../../lib/constants";

const ModalWrapper = styled(Modal)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export function DesignDrawingDropdown({
  department,
  setFormData,
  formData,
  jobCode,
  values,
  drawingCounts,
  required = false,
  open = false,
  onClose = () => {},
  taskList = [],
}: { department?: any; setFormData?: any; formData?: any; jobCode?: any; values?: any; drawingCounts?: any; required?: any; open?: any; onClose?: any; taskList?: any }) {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const departmentStr = department === Departments.DESIGN ? "Design" : "Drawing";
  const [showTaskAssignModal, setShowTaskAssignModal] = useState(false);

  const createOptions = useCallback(
    (prefix, count) =>
      Array.from({ length: count }, (_, i) => {
        const index = i + 1;
        return [
          { label: `${prefix}${index}`, isLabel: true },
          { value: `${prefix}${index}.1`, label: `${prefix}${index}.1 ${departmentStr}` },
          { value: `${prefix}${index}.2`, label: `${prefix}${index}.2 ${departmentStr}` },
        ];
      }).flat(),
    [departmentStr]
  );

  const groupedOptions = useMemo(() => {
    const newOptions = [
      ...createOptions("C", drawingCounts.columnFootingDrawings),
      ...createOptions("S", drawingCounts.structuralDrawings),
    ];
    return newOptions;
  }, [createOptions, drawingCounts.columnFootingDrawings, drawingCounts.structuralDrawings]);

  const options = useMemo(() => groupedOptions.filter((option) => !option.isLabel), [groupedOptions]);
  const selectedValues = useMemo(
    () =>
      selectedOptions?.length > 0
        ? selectedOptions.map((option) => option.selected && option.value).filter(Boolean)
        : [],
    [selectedOptions]
  );
  const allSelected = useMemo(
    () => selectedValues.filter(Boolean).length === options.length,
    [selectedValues, options.length]
  );

  const handleSelectAll = () =>
    setSelectedOptions(allSelected ? [] : options.map((option) => ({ ...option, selected: true })));

  const handleSelect = (value) => {
    console.log(value);
    const isSelected = selectedValues.includes(value);
    let selected = isSelected ? selectedValues.filter((v) => v !== value) : [...selectedValues, value];
    console.log(selected);
    const selectedOpts = options.map((option) => {
      (option as any).selected = selected.includes((option as any).value);
      return option;
    });
    console.log(selectedOpts);
    setSelectedOptions(selectedOpts);

    setFormData((prev) => ({ ...prev, tasks: selectedOpts.filter((option: any) => (option as any).selected) }));
  };

  const handleInputChange = (e, label) => {
    const { name, value } = e.target;
    const currentOption = options.find((option) => option.label === label);
    currentOption[name] = value;
    setSelectedOptions((prev) => prev.map((option) => (option.label === label ? currentOption : option)));
    setFormData((prev) => ({
      ...prev,
      tasks: prev.tasks.map((task) => (task.label === label ? { ...task, [name]: value } : task)),
    }));
  };

  return (
    <>
      <button type="button" className="btn as-dropdown" onClick={() => setShowTaskAssignModal(true)}>
        {selectedValues.length > 0 ? (
          <div className="row align_start">
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, fontSize: "0.875rem", maxWidth: "23rem" }}>
              {selectedValues.filter(Boolean).map((value) => (
                <Chip key={value} label={value} size="small" sx={{ fontSize: "0.625rem" }} />
              ))}
            </Box>
            <Icon icon="mingcute:down-line" className="dropdown-icon" />
          </div>
        ) : (
          <>
            <span>Assign Task</span>
            <Icon icon="mingcute:down-line" className="dropdown-icon with-button" />
          </>
        )}
      </button>
      <ModalWrapper
        open={showTaskAssignModal}
        onClose={() => setShowTaskAssignModal(false)}
        aria-labelledby="assign-task-modal"
        aria-describedby="assign-task-modal"
      >
        <div className="modal_container full column task-assign-modal">
          <div className="column">
            <div className="small font-md">Select Tasks</div>
            <Box sx={{ paddingInline: "1rem" }}>
              <label htmlFor={"select_all"} className="label">
                <input
                  type="checkbox"
                  name="select_all"
                  id={"select_all"}
                  checked={allSelected}
                  onChange={handleSelectAll}
                />
                Select All
              </label>
            </Box>
            {groupedOptions.length > 0 && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "4rem 1fr 1fr",
                  columnGap: 2,
                }}
              >
                {groupedOptions.map((option) =>
                  option.isLabel ? (
                    <ListSubheader key={option.label}>{option.label}:</ListSubheader>
                  ) : (
                    <Box
                      key={option.value}
                      sx={{
                        py: "0.25rem",
                        display: "grid",
                        alignItems: "center",
                        gridTemplateColumns: "1fr 1fr",
                        rowGap: 1,
                      }}
                    >
                      <label htmlFor={option.value} className="label">
                        <input
                          type="checkbox"
                          name={option.value}
                          id={option.value}
                          checked={selectedValues.includes(option.value)}
                          onChange={() => handleSelect(option.value)}
                        />
                        {option.label}
                      </label>

                      {selectedValues?.length > 0 && selectedValues?.includes(option.value) && (
                        <Box
                          sx={{
                            display: "flex",
                            ml: 1,
                            fontSize: "0.875rem",
                            "& .input": { maxWidth: "3.5rem" },
                            gap: 0.5,
                          }}
                        >
                          <input
                            type="number"
                            placeholder="hrs"
                            name="hours"
                            className="input shrink"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            onChange={(e) => handleInputChange(e, option.label)}
                            value={(option as any).hours}
                            min={0}
                            max={23}
                          />
                          <span>:</span>
                          <input
                            type="number"
                            placeholder="mins"
                            name="minutes"
                            className="input shrink"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            onChange={(e) => handleInputChange(e, option.label)}
                            value={(option as any).minutes}
                            min={0}
                            max={59}
                          />
                        </Box>
                      )}
                    </Box>
                  )
                )}
              </Box>
            )}
          </div>
        </div>
      </ModalWrapper>
    </>
  );

  // return (
  //   <Select
  //     id={`${jobCode}-${department}-assignment`}
  //     onChange={(e) => handleSelect(e.target.value)}
  //     value={selectedValues}
  //     multiple
  //     required={required}
  //     input={<SelectInput />}
  //     displayEmpty
  //     sx={{ fontSize: "0.875rem", minWidth: "15rem", maxWidth: "20rem" }}
  //     renderValue={(selected) =>
  //       selected && selected.length > 0 ? (
  //         <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, fontSize: "0.875rem" }}>
  //           {selected.map((value) => (
  //             <Chip
  //               key={value}
  //               label={options.find((option) => option.value === value).value}
  //               size="small"
  //               sx={{ fontSize: "0.625rem" }}
  //             />
  //           ))}
  //         </Box>
  //       ) : (
  //         <Typography sx={{ fontSize: "0.875rem" }}>{`Select ${departmentStr}`}</Typography>
  //       )
  //     }
  //     MenuProps={{
  //       PaperProps: {
  //         sx: {
  //           maxHeight: 48 * 4.5 + 8,
  //           ".MuiList-root": {
  //             display: "grid",
  //             gridTemplateColumns: "3rem 1fr",
  //             "& :first-of-type": { gridColumn: "span 2" },
  //             "& :nth-of-type(3n+2)": { gridRow: "span 2" },
  //           },
  //         },
  //       },
  //     }}
  //   >
  //     <MenuItem value="*" sx={{ py: "0.25rem", display: "flex" }}>
  //       <ListItemIcon>
  //         <Checkbox checked={allSelected} />
  //       </ListItemIcon>
  //       <ListItemText primary="Select All" sx={{ "& .MuiTypography-root": { fontSize: "0.875rem" } }} />
  //     </MenuItem>
  //     {groupedOptions.length > 0 &&
  //       groupedOptions.map((option) =>
  //         option.isLabel ? (
  //           <ListSubheader key={option.label}>{option.label}:</ListSubheader>
  //         ) : (
  //           <MenuItem key={option.value} value={option.value} sx={{ py: "0.25rem", display: "flex" }}>
  //             <ListItemIcon>
  //               <Checkbox checked={selectedValues.includes(option.value)} />
  //             </ListItemIcon>
  //             <ListItemText primary={option.label} sx={{ "& .MuiTypography-root": { fontSize: "0.875rem" } }} />
  //             {selectedValues.includes(option.value) && (
  //               <Box
  //                 sx={{
  //                   display: "flex",
  //                   ml: 1,
  //                   fontSize: "0.875rem",
  //                   "& .input": { maxWidth: "3.5rem" },
  //                   gap: 0.5,
  //                 }}
  //               >
  //                 <input
  //                   type="text"
  //                   placeholder="hrs"
  //                   name="hours"
  //                   className="input shrink"
  //                   onClick={(e) => e.stopPropagation()}
  //                   onKeyDown={(e) => e.stopPropagation()}
  //                   onChange={(e) => handleInputChange(e, option.label)}
  //                 />
  //                 <span>:</span>
  //                 <input
  //                   type="text"
  //                   placeholder="mins"
  //                   name="minutes"
  //                   className="input shrink"
  //                   onClick={(e) => e.stopPropagation()}
  //                   onKeyDown={(e) => e.stopPropagation()}
  //                   onChange={(e) => handleInputChange(e, option.label)}
  //                 />
  //               </Box>
  //             )}
  //           </MenuItem>
  //         )
  //       )}
  //   </Select>
  // );
}
